import {
	createContext,
	useContext,
	useMemo,
	FC,
	ReactNode,
	useState,
	useCallback,
	useEffect,
} from 'react';
import { MeProfile } from '../../../shared/MySrcm';
import { ApplicantAdditionalDetailsType } from '../../../components/AdditionalDetailsType';
import { CategoryAnswerType } from './CategoryAnswerType';
import { TermsAndConditions } from './TermsAndConditionsType';
import {
	getApplication,
	saveApplication,
	submitNewApplication,
} from '../../../service/VolunteerApplicationService';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	transformAdditionalDetailsToGRPC,
	transformAnswerToGRPC,
	transformGRPCApplicationToAdditionalDetails,
	transformGRPCApplicationToFamilyMemberDetails,
	transformGRPCApplicationToOtherReferenceDetails,
	transformGRPCApplicationToPreceptorContactDetails,
	transformGRPCApplicationToTrainingCourseDetails,
	transformMySrcmProfileToGRPCUser,
} from '../../../shared/Utils';
import {
	Application,
	DraftState,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { EvaluationCategoryID } from '../../../shared/Constants';
import { useCallApi } from '../../../hooks/useCallApi';
import { TrainingCourseDetailsType } from '../../../components/TrainingCourseDetailsType';
import { PreceptorContactDetailsType } from '../../../components/PreceptorContactDetailsType';
import { FamilyMemberDetailsType } from '../../../components/FamilyMemberDetailsType';
import { OtherReferenceDetailsType } from '../../../components/OtherReferenceDetailsType';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { useTranslation } from 'react-i18next';
import { AboutTheProcessType } from './AboutTheProcessType';
import { useApplicantHome } from '../useApplicantHome';

export interface NewApplicationContextType {
	mySrcmProfile: MeProfile;
	setMySrcmProfile: (mySrcmProfile: MeProfile) => void;
	additionalApplicantDetails: ApplicantAdditionalDetailsType;
	setAdditionalApplicantDetails: (
		additionalDetails: ApplicantAdditionalDetailsType
	) => void;
	trainingCourseDetails: TrainingCourseDetailsType;
	setTrainingCourseDetails: (
		trainingDetails: TrainingCourseDetailsType
	) => void;
	preceptorContactDetails: PreceptorContactDetailsType;
	setPreceptorContactDetails: (
		preceptorDetails: PreceptorContactDetailsType
	) => void;
	familyMemberContactDetails: FamilyMemberDetailsType;
	setFamilyMemberContactDetails: (
		familyMemberDetails: FamilyMemberDetailsType
	) => void;
	otherReferenceContactDetails: OtherReferenceDetailsType;
	setOtherReferenceContactDetails: (
		otherReferenceDetails: OtherReferenceDetailsType
	) => void;
	aboutTheProcess: AboutTheProcessType;
	setAboutTheProcess: (wsc: AboutTheProcessType) => void;
	basicEligibilityCategoryAnswers: CategoryAnswerType;
	setBasicEligibilityCategoryAnswers: (answers: CategoryAnswerType) => void;
	inspirationAndWillingnessCategoryAnswers: CategoryAnswerType;
	setInspirationAndWillingnessCategoryAnswers: (
		answers: CategoryAnswerType
	) => void;
	practiceCategoryAnswers: CategoryAnswerType;
	setPracticeCategoryAnswers: (answers: CategoryAnswerType) => void;
	characterAndHabitsCategoryAnswers: CategoryAnswerType;
	setCharacterAndHabitsCategoryAnswers: (answers: CategoryAnswerType) => void;
	availabilityAfterProgramCategoryAnswers: CategoryAnswerType;
	setAvailabilityAfterProgramCategoryAnswers: (
		answers: CategoryAnswerType
	) => void;
	supportCategoryAnswers: CategoryAnswerType;
	setSupportCategoryAnswers: (answers: CategoryAnswerType) => void;
	understandingOfHeartfulnessCategoryAnswers: CategoryAnswerType;
	setUnderstandingOfHeartfulnessCategoryAnswers: (
		answers: CategoryAnswerType
	) => void;
	dietaryHabitsAndHealthCategoryAnswers: CategoryAnswerType;
	setDietaryHabitsAndHealthCategoryAnswers: (
		answers: CategoryAnswerType
	) => void;
	termsAndConditions: TermsAndConditions;
	setTermsAndConditions: (tnc: TermsAndConditions) => void;
	onSubmitClick: () => void;
	onSaveandNextClick: (
		draftState: DraftState,
		completedDraftState: DraftState,
		applicationId: number
	) => Promise<void>;
	isEmailVerified: boolean;
	setIsEmailVerified: (isEmailVerified: boolean) => void;
	applicationDetails: Application | null;
	setApplicationDetails: (details: Application) => void;
}

interface Props {
	children: ReactNode;
}

const NewApplicationContext = createContext<NewApplicationContextType>({
	mySrcmProfile: {},
	setMySrcmProfile: () => {},
	additionalApplicantDetails: {},
	setAdditionalApplicantDetails: () => {},
	trainingCourseDetails: {},
	setTrainingCourseDetails: () => {},
	preceptorContactDetails: {},
	setPreceptorContactDetails: () => {},
	familyMemberContactDetails: {},
	setFamilyMemberContactDetails: () => {},
	otherReferenceContactDetails: {},
	setOtherReferenceContactDetails: () => {},
	aboutTheProcess: {},
	setAboutTheProcess: () => {},
	basicEligibilityCategoryAnswers: {},
	setBasicEligibilityCategoryAnswers: () => {},
	inspirationAndWillingnessCategoryAnswers: {},
	setInspirationAndWillingnessCategoryAnswers: () => {},
	practiceCategoryAnswers: {},
	setPracticeCategoryAnswers: () => {},
	characterAndHabitsCategoryAnswers: {},
	setCharacterAndHabitsCategoryAnswers: () => {},
	availabilityAfterProgramCategoryAnswers: {},
	setAvailabilityAfterProgramCategoryAnswers: () => {},
	supportCategoryAnswers: {},
	setSupportCategoryAnswers: () => {},
	understandingOfHeartfulnessCategoryAnswers: {},
	setUnderstandingOfHeartfulnessCategoryAnswers: () => {},
	dietaryHabitsAndHealthCategoryAnswers: {},
	setDietaryHabitsAndHealthCategoryAnswers: () => {},
	termsAndConditions: {},
	setTermsAndConditions: () => {},
	onSubmitClick: () => {},
	onSaveandNextClick: async () => {},
	isEmailVerified: false,
	setIsEmailVerified: () => {},
	applicationDetails: null,
	setApplicationDetails: () => {},
});

export const NewApplicationProvider: FC<Props> = ({ children }: Props) => {
	const [mySrcmProfile, setMySrcmProfile] = useState<MeProfile>({});
	const [additionalApplicantDetails, setAdditionalApplicantDetails] =
		useState<ApplicantAdditionalDetailsType>({});
	const [trainingCourseDetails, setTrainingCourseDetails] =
		useState<TrainingCourseDetailsType>({});
	const [preceptorContactDetails, setPreceptorContactDetails] =
		useState<PreceptorContactDetailsType>({});
	const [familyMemberContactDetails, setFamilyMemberContactDetails] =
		useState<FamilyMemberDetailsType>({});
	const [otherReferenceContactDetails, setOtherReferenceContactDetails] =
		useState<OtherReferenceDetailsType>({});
	const [aboutTheProcess, setAboutTheProcess] = useState<AboutTheProcessType>(
		{}
	);
	const [basicEligibilityCategoryAnswers, setBasicEligibilityCategoryAnswers] =
		useState<CategoryAnswerType>({});
	const [
		inspirationAndWillingnessCategoryAnswers,
		setInspirationAndWillingnessCategoryAnswers,
	] = useState<CategoryAnswerType>({});
	const [practiceCategoryAnswers, setPracticeCategoryAnswers] =
		useState<CategoryAnswerType>({});
	const [
		characterAndHabitsCategoryAnswers,
		setCharacterAndHabitsCategoryAnswers,
	] = useState<CategoryAnswerType>({});
	const [
		availabilityAfterProgramCategoryAnswers,
		setAvailabilityAfterProgramCategoryAnswers,
	] = useState<CategoryAnswerType>({});
	const [supportCategoryAnswers, setSupportCategoryAnswers] =
		useState<CategoryAnswerType>({});
	const [
		understandingOfHeartfulnessCategoryAnswers,
		setUnderstandingOfHeartfulnessCategoryAnswers,
	] = useState<CategoryAnswerType>({});
	const [
		dietaryHabitsAndHealthCategoryAnswers,
		setDietaryHabitsAndHealthCategoryAnswers,
	] = useState<CategoryAnswerType>({});
	const [termsAndConditions, setTermsAndConditions] =
		useState<TermsAndConditions>({});
	const { t, i18n } = useTranslation();
	const { batchId } = useBatchId();
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();
	const { callApi } = useCallApi();
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [applicationDetails, setApplicationDetails] =
		useState<Application | null>(null);
	const { applicationDetails: applicationDetailsFromHome } = useApplicantHome();

	useEffect(() => {
		setApplicationDetails(applicationDetailsFromHome);
	}, [applicationDetailsFromHome]);

	const categoryAnswerTypesFromApplication = useCallback(
		(application: Application | null) => {
			const inspirationAndWillingnessCategoryAnswerTypeObject: CategoryAnswerType =
				{};
			const practiceCategoryAnswerTypeObject: CategoryAnswerType = {};
			const availabilityAfterProgramCategoryAnswerTypeObject: CategoryAnswerType =
				{};
			const characterAndHabitsCategoryAnswerTypeObject: CategoryAnswerType = {};
			const supportCategoryAnswerTypeObject: CategoryAnswerType = {};
			const understandingOfHeartfulnessCategoryAnswerTypeObject: CategoryAnswerType =
				{};
			const dietaryHabitsAndHealthCategoryAnswerTypeObject: CategoryAnswerType =
				{};
			const eligibilityCriteriaCategoryAnswerTypeObject: CategoryAnswerType =
				{};

			if (application) {
				const questionsAndAnswers =
					application?.userAnswers?.questionsAndAnswers || [];

				questionsAndAnswers.forEach((question) => {
					if (
						question.categoryId ===
						EvaluationCategoryID.INSPIRATION_AND_WILLINGNESS
					) {
						inspirationAndWillingnessCategoryAnswerTypeObject[
							question.questionId
						] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (question.categoryId === EvaluationCategoryID.PRACTICE) {
						practiceCategoryAnswerTypeObject[question.questionId] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (
						question.categoryId ===
						EvaluationCategoryID.AVAILABILITY_AFTER_PROGRAM
					) {
						availabilityAfterProgramCategoryAnswerTypeObject[
							question.questionId
						] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (
						question.categoryId === EvaluationCategoryID.CHARACTER_AND_HABITS
					) {
						characterAndHabitsCategoryAnswerTypeObject[question.questionId] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (question.categoryId === EvaluationCategoryID.SUPPORT) {
						supportCategoryAnswerTypeObject[question.questionId] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (
						question.categoryId ===
						EvaluationCategoryID.UNDERSTANDING_OF_HEARTFULNESS
					) {
						understandingOfHeartfulnessCategoryAnswerTypeObject[
							question.questionId
						] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (
						question.categoryId ===
						EvaluationCategoryID.DIETARY_HABITS_AND_HEALTH
					) {
						dietaryHabitsAndHealthCategoryAnswerTypeObject[
							question.questionId
						] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					} else if (
						question.categoryId === EvaluationCategoryID.BASIC_ELIGIBILITY
					) {
						eligibilityCriteriaCategoryAnswerTypeObject[question.questionId] = {
							selectedOption: question?.answerOptionId,
							comments: question?.additionalComment,
						};
					}
				});
			}

			return {
				eligibilityCriteriaCategoryAnswerTypeObject,
				inspirationAndWillingnessCategoryAnswerTypeObject,
				practiceCategoryAnswerTypeObject,
				characterAndHabitsCategoryAnswerTypeObject,
				availabilityAfterProgramCategoryAnswerTypeObject,
				supportCategoryAnswerTypeObject,
				understandingOfHeartfulnessCategoryAnswerTypeObject,
				dietaryHabitsAndHealthCategoryAnswerTypeObject,
			};
		},
		[]
	);

	useEffect(() => {
		const {
			eligibilityCriteriaCategoryAnswerTypeObject,
			inspirationAndWillingnessCategoryAnswerTypeObject,
			practiceCategoryAnswerTypeObject,
			characterAndHabitsCategoryAnswerTypeObject,
			availabilityAfterProgramCategoryAnswerTypeObject,
			supportCategoryAnswerTypeObject,
			understandingOfHeartfulnessCategoryAnswerTypeObject,
			dietaryHabitsAndHealthCategoryAnswerTypeObject,
		} = categoryAnswerTypesFromApplication(applicationDetails);

		setBasicEligibilityCategoryAnswers(
			eligibilityCriteriaCategoryAnswerTypeObject
		);
		setInspirationAndWillingnessCategoryAnswers(
			inspirationAndWillingnessCategoryAnswerTypeObject
		);
		setPracticeCategoryAnswers(practiceCategoryAnswerTypeObject);
		setCharacterAndHabitsCategoryAnswers(
			characterAndHabitsCategoryAnswerTypeObject
		);
		setAvailabilityAfterProgramCategoryAnswers(
			availabilityAfterProgramCategoryAnswerTypeObject
		);
		setSupportCategoryAnswers(supportCategoryAnswerTypeObject);
		setUnderstandingOfHeartfulnessCategoryAnswers(
			understandingOfHeartfulnessCategoryAnswerTypeObject
		);
		setDietaryHabitsAndHealthCategoryAnswers(
			dietaryHabitsAndHealthCategoryAnswerTypeObject
		);
	}, [applicationDetails, categoryAnswerTypesFromApplication]);

	const transformAnswersForSubmitting = useCallback(() => {
		const {
			eligibilityCriteriaCategoryAnswerTypeObject,
			inspirationAndWillingnessCategoryAnswerTypeObject,
			practiceCategoryAnswerTypeObject,
			characterAndHabitsCategoryAnswerTypeObject,
			availabilityAfterProgramCategoryAnswerTypeObject,
			supportCategoryAnswerTypeObject,
			understandingOfHeartfulnessCategoryAnswerTypeObject,
			dietaryHabitsAndHealthCategoryAnswerTypeObject,
		} = categoryAnswerTypesFromApplication(applicationDetails);

		return [
			...transformAnswerToGRPC(
				EvaluationCategoryID.BASIC_ELIGIBILITY,
				eligibilityCriteriaCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.INSPIRATION_AND_WILLINGNESS,
				inspirationAndWillingnessCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.PRACTICE,
				practiceCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.CHARACTER_AND_HABITS,
				characterAndHabitsCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.AVAILABILITY_AFTER_PROGRAM,
				availabilityAfterProgramCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.SUPPORT,
				supportCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.UNDERSTANDING_OF_HEARTFULNESS,
				understandingOfHeartfulnessCategoryAnswerTypeObject
			),
			...transformAnswerToGRPC(
				EvaluationCategoryID.DIETARY_HABITS_AND_HEALTH,
				dietaryHabitsAndHealthCategoryAnswerTypeObject
			),
		];
	}, [applicationDetails, categoryAnswerTypesFromApplication]);

	const transformAnswersForSaving = useCallback(
		(draftState: DraftState) => {
			const {
				eligibilityCriteriaCategoryAnswerTypeObject,
				inspirationAndWillingnessCategoryAnswerTypeObject,
				practiceCategoryAnswerTypeObject,
				characterAndHabitsCategoryAnswerTypeObject,
				availabilityAfterProgramCategoryAnswerTypeObject,
				supportCategoryAnswerTypeObject,
				understandingOfHeartfulnessCategoryAnswerTypeObject,
				dietaryHabitsAndHealthCategoryAnswerTypeObject,
			} = categoryAnswerTypesFromApplication(applicationDetails);

			return [
				...transformAnswerToGRPC(
					EvaluationCategoryID.BASIC_ELIGIBILITY,
					draftState === DraftState.INSPIRATION_AND_WILLINGNESS
						? basicEligibilityCategoryAnswers
						: eligibilityCriteriaCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.INSPIRATION_AND_WILLINGNESS,
					draftState === DraftState.PRACTICE
						? inspirationAndWillingnessCategoryAnswers
						: inspirationAndWillingnessCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.PRACTICE,
					draftState === DraftState.CHARACTER_AND_HABITS
						? practiceCategoryAnswers
						: practiceCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.CHARACTER_AND_HABITS,
					draftState === DraftState.AVAILABILITY_AFTER_PROGRAM
						? characterAndHabitsCategoryAnswers
						: characterAndHabitsCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.AVAILABILITY_AFTER_PROGRAM,
					draftState === DraftState.FAMILY_SUPPORT
						? availabilityAfterProgramCategoryAnswers
						: availabilityAfterProgramCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.SUPPORT,
					draftState === DraftState.UNDERSTANDING_OF_HEARTFULNESS
						? supportCategoryAnswers
						: supportCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.UNDERSTANDING_OF_HEARTFULNESS,
					draftState === DraftState.DIETARY_HABITS_AND_HEALTH
						? understandingOfHeartfulnessCategoryAnswers
						: understandingOfHeartfulnessCategoryAnswerTypeObject
				),
				...transformAnswerToGRPC(
					EvaluationCategoryID.DIETARY_HABITS_AND_HEALTH,
					draftState === DraftState.TERMS_AND_CONDITIONS
						? dietaryHabitsAndHealthCategoryAnswers
						: dietaryHabitsAndHealthCategoryAnswerTypeObject
				),
			];
		},
		[
			applicationDetails,
			availabilityAfterProgramCategoryAnswers,
			basicEligibilityCategoryAnswers,
			categoryAnswerTypesFromApplication,
			characterAndHabitsCategoryAnswers,
			dietaryHabitsAndHealthCategoryAnswers,
			inspirationAndWillingnessCategoryAnswers,
			practiceCategoryAnswers,
			supportCategoryAnswers,
			understandingOfHeartfulnessCategoryAnswers,
		]
	);

	const transformApplicantDetails = useCallback(
		(application: Application | null, draftState: DraftState) => {
			let additionalDetailsFromApplication: ApplicantAdditionalDetailsType = {};
			let trainingDetailsFromApplication: TrainingCourseDetailsType = {};
			let preceptorDetailsFromApplication: PreceptorContactDetailsType = {};
			let familyDetailsFromApplication: FamilyMemberDetailsType = {};
			let otherRefDetailsFromApplication: OtherReferenceDetailsType = {};

			if (application?.applicant) {
				additionalDetailsFromApplication =
					transformGRPCApplicationToAdditionalDetails(application);
				trainingDetailsFromApplication =
					transformGRPCApplicationToTrainingCourseDetails(application);
				preceptorDetailsFromApplication =
					transformGRPCApplicationToPreceptorContactDetails(application);
				familyDetailsFromApplication =
					transformGRPCApplicationToFamilyMemberDetails(application);
				otherRefDetailsFromApplication =
					transformGRPCApplicationToOtherReferenceDetails(application);
			}

			const applicantDetails: ApplicantAdditionalDetailsType =
				draftState === DraftState.TRAINING_COURSE
					? {
							...additionalApplicantDetails,
							center: mySrcmProfile.srcm_group,
					  }
					: {
							...additionalDetailsFromApplication,
							center: mySrcmProfile.srcm_group,
					  };

			const trainingDetails: TrainingCourseDetailsType =
				draftState === DraftState.PRECEPTOR_CONTACT
					? {
							...trainingCourseDetails,
					  }
					: { ...trainingDetailsFromApplication };

			const preceptorDetails: PreceptorContactDetailsType = {
				...preceptorDetailsFromApplication,
				...preceptorContactDetails,
			};

			const familyDetails: FamilyMemberDetailsType =
				draftState === DraftState.OTHER_DETAILS
					? {
							...familyMemberContactDetails,
					  }
					: { ...familyDetailsFromApplication };

			const otherRefDetails: OtherReferenceDetailsType =
				draftState === DraftState.ABOUT_THE_PROCESS
					? {
							...otherReferenceContactDetails,
					  }
					: { ...otherRefDetailsFromApplication };

			return {
				applicantDetails,
				trainingDetails,
				preceptorDetails,
				familyDetails,
				otherRefDetails,
			};
		},
		[
			additionalApplicantDetails,
			familyMemberContactDetails,
			mySrcmProfile.srcm_group,
			otherReferenceContactDetails,
			preceptorContactDetails,
			trainingCourseDetails,
		]
	);

	const onConfirmSubmit = useCallback(async () => {
		const answers = transformAnswersForSubmitting();

		let additionalDetailsFromApplication: ApplicantAdditionalDetailsType = {};
		let trainingDetailsFromApplication: TrainingCourseDetailsType = {};
		let preceptorDetailsFromApplication: PreceptorContactDetailsType = {};
		let familyDetailsFromApplication: FamilyMemberDetailsType = {};
		let otherRefDetailsFromApplication: OtherReferenceDetailsType = {};

		if (applicationDetails?.applicant) {
			additionalDetailsFromApplication =
				transformGRPCApplicationToAdditionalDetails(applicationDetails);
			trainingDetailsFromApplication =
				transformGRPCApplicationToTrainingCourseDetails(applicationDetails);
			preceptorDetailsFromApplication =
				transformGRPCApplicationToPreceptorContactDetails(applicationDetails);
			familyDetailsFromApplication =
				transformGRPCApplicationToFamilyMemberDetails(applicationDetails);
			otherRefDetailsFromApplication =
				transformGRPCApplicationToOtherReferenceDetails(applicationDetails);
		}

		const preceptorDetails: PreceptorContactDetailsType = {
			...preceptorDetailsFromApplication,
			...preceptorContactDetails,
		};

		const request = {
			userDetails: transformMySrcmProfileToGRPCUser(mySrcmProfile),
			additionalDetails: transformAdditionalDetailsToGRPC(
				additionalDetailsFromApplication,
				trainingDetailsFromApplication,
				preceptorDetails,
				familyDetailsFromApplication,
				otherRefDetailsFromApplication
			),
			userAnswers: {
				questionsAndAnswers: answers,
				language: i18n.language,
			},
			batchId,
		};

		try {
			await callApi(
				() => submitNewApplication(request),
				'errorOccurredWhileSubmittingApplication'
			);
			infoPopUpAction(
				'submittedSuccessfully',
				'submittedApplicationSuccessfully',
				() => {
					window.location.reload();
				},
				ConfirmTick
			);
		} catch (e) {
			// handled in callApi
		}
	}, [
		transformAnswersForSubmitting,
		applicationDetails,
		batchId,
		callApi,
		i18n.language,
		mySrcmProfile,
		infoPopUpAction,
		preceptorContactDetails,
	]);

	const onSubmitClick = useCallback(() => {
		let applicantDetails: ApplicantAdditionalDetailsType = {};
		let trainingDetails: TrainingCourseDetailsType = {};
		let preceptorDetails: PreceptorContactDetailsType = {};
		let familyDetails: FamilyMemberDetailsType = {};
		let otherRefDetails: OtherReferenceDetailsType = {};

		if (applicationDetails?.applicant) {
			applicantDetails =
				transformGRPCApplicationToAdditionalDetails(applicationDetails);
			trainingDetails =
				transformGRPCApplicationToTrainingCourseDetails(applicationDetails);
			preceptorDetails =
				transformGRPCApplicationToPreceptorContactDetails(applicationDetails);
			familyDetails =
				transformGRPCApplicationToFamilyMemberDetails(applicationDetails);
			otherRefDetails =
				transformGRPCApplicationToOtherReferenceDetails(applicationDetails);
		}

		const categoryData = [
			{ key: 'additionalApplicantDetails', data: applicantDetails },
			{ key: 'trainingCourseDetails', data: trainingDetails },
			{ key: 'preceptorContactDetails', data: preceptorDetails },
			{ key: 'familyMemberContactDetails', data: familyDetails },
			{
				key: 'otherReferenceContactDetails',
				data: otherRefDetails,
			},
			{
				key: 'availabilityAfterProgramCategoryAnswers',
				data: availabilityAfterProgramCategoryAnswers,
			},
			{
				key: 'basicEligibilityCategoryAnswers',
				data: basicEligibilityCategoryAnswers,
			},
			{
				key: 'inspirationAndWillingnessCategoryAnswers',
				data: inspirationAndWillingnessCategoryAnswers,
			},
			{ key: 'practiceCategoryAnswers', data: practiceCategoryAnswers },
			{
				key: 'characterAndHabitsCategoryAnswers',
				data: characterAndHabitsCategoryAnswers,
			},
			{ key: 'supportCategoryAnswers', data: supportCategoryAnswers },
			{
				key: 'understandingOfHeartfulnessCategoryAnswers',
				data: understandingOfHeartfulnessCategoryAnswers,
			},
			{
				key: 'dietaryHabitsAndHealthCategoryAnswers',
				data: dietaryHabitsAndHealthCategoryAnswers,
			},
		];

		const emptyCategories = categoryData
			.filter(({ key, data }) => {
				if (!data || Object.keys(data).length === 0) return true;
				if (key === 'otherReferenceContactDetails') {
					const otherReferenceData = data as OtherReferenceDetailsType;
					if (!otherReferenceData.otherReferenceProvided) return false;
				}
				return Object.values(data).every((value) => {
					if (value == null || value === '') return true;
					if (typeof value === 'object' && value !== null) {
						return Object.values(value as object).every(
							(innerValue) => innerValue == null || innerValue === ''
						);
					}
					return false;
				});
			})
			.map(({ key }) => t(key));

		if (emptyCategories.length === 0) {
			confirmPopUpAction(
				'confirmSubmit',
				'applicationOnceSubmittedCanNotBeChanged',
				onConfirmSubmit
			);
		} else {
			infoPopUpAction(
				'incompleteFormSubmission',
				`${t('pleaseAnswerAllRequiredQuestions')} ${emptyCategories.join(
					', '
				)}`,
				() => {}
			);
		}
	}, [
		applicationDetails,
		confirmPopUpAction,
		infoPopUpAction,
		onConfirmSubmit,
		basicEligibilityCategoryAnswers,
		inspirationAndWillingnessCategoryAnswers,
		practiceCategoryAnswers,
		characterAndHabitsCategoryAnswers,
		availabilityAfterProgramCategoryAnswers,
		supportCategoryAnswers,
		understandingOfHeartfulnessCategoryAnswers,
		dietaryHabitsAndHealthCategoryAnswers,
		t,
	]);

	const onSaveandNextClick = useCallback(
		async (
			draftState: DraftState,
			completedDraftState: DraftState,
			applicationId: number
		) => {
			const answers = transformAnswersForSaving(draftState);
			const {
				applicantDetails,
				trainingDetails,
				preceptorDetails,
				familyDetails,
				otherRefDetails,
			} = transformApplicantDetails(applicationDetails, draftState);

			const request = {
				additionalDetails: transformAdditionalDetailsToGRPC(
					applicantDetails,
					trainingDetails,
					preceptorDetails,
					familyDetails,
					otherRefDetails
				),
				userDetails: transformMySrcmProfileToGRPCUser(mySrcmProfile),
				userAnswers: {
					questionsAndAnswers: answers,
					language: i18n.language,
				},
				applicationId,
				batchId,
				draftState,
				completedDraftState,
			};

			try {
				const savedApplicationId = await callApi(
					() => saveApplication(request),
					'errorOccurredWhileSubmittingApplication'
				);
				if (savedApplicationId) {
					const data = await getApplication({
						applicationId: savedApplicationId,
					});
					setApplicationDetails(data);
				}
			} catch (e) {
				// handled in callApi
			}
		},
		[
			applicationDetails,
			mySrcmProfile,
			i18n.language,
			batchId,
			callApi,
			transformAnswersForSaving,
			transformApplicantDetails,
		]
	);

	const value = useMemo<NewApplicationContextType>(() => {
		return {
			mySrcmProfile,
			setMySrcmProfile,
			additionalApplicantDetails,
			setAdditionalApplicantDetails,
			trainingCourseDetails,
			setTrainingCourseDetails,
			preceptorContactDetails,
			setPreceptorContactDetails,
			familyMemberContactDetails,
			setFamilyMemberContactDetails,
			otherReferenceContactDetails,
			aboutTheProcess,
			setAboutTheProcess,
			setOtherReferenceContactDetails,
			basicEligibilityCategoryAnswers,
			setBasicEligibilityCategoryAnswers,
			inspirationAndWillingnessCategoryAnswers,
			setInspirationAndWillingnessCategoryAnswers,
			practiceCategoryAnswers,
			setPracticeCategoryAnswers,
			characterAndHabitsCategoryAnswers,
			setCharacterAndHabitsCategoryAnswers,
			availabilityAfterProgramCategoryAnswers,
			setAvailabilityAfterProgramCategoryAnswers,
			supportCategoryAnswers,
			setSupportCategoryAnswers,
			understandingOfHeartfulnessCategoryAnswers,
			setUnderstandingOfHeartfulnessCategoryAnswers,
			dietaryHabitsAndHealthCategoryAnswers,
			setDietaryHabitsAndHealthCategoryAnswers,
			termsAndConditions,
			setTermsAndConditions,
			onSubmitClick,
			onSaveandNextClick,
			isEmailVerified,
			setIsEmailVerified,
			applicationDetails,
			setApplicationDetails,
		};
	}, [
		mySrcmProfile,
		additionalApplicantDetails,
		trainingCourseDetails,
		preceptorContactDetails,
		familyMemberContactDetails,
		otherReferenceContactDetails,
		aboutTheProcess,
		basicEligibilityCategoryAnswers,
		inspirationAndWillingnessCategoryAnswers,
		practiceCategoryAnswers,
		characterAndHabitsCategoryAnswers,
		availabilityAfterProgramCategoryAnswers,
		supportCategoryAnswers,
		understandingOfHeartfulnessCategoryAnswers,
		dietaryHabitsAndHealthCategoryAnswers,
		termsAndConditions,
		isEmailVerified,
		onSubmitClick,
		onSaveandNextClick,
		applicationDetails,
	]);

	return (
		<NewApplicationContext.Provider value={value}>
			{children}
		</NewApplicationContext.Provider>
	);
};

export const useNewApplication = () => {
	return useContext(NewApplicationContext);
};
